import { useMemo } from 'react';
import { useAllocationReportQuery } from '../api/queries';

type Props = {
  onDrillIn?: (afNumber: string) => void;
};

/**
 * Network-level roll-up of per-territory paid-budget allocations (Google vs
 * Meta). Admin-only. Territories with no saved split are 100% Google by default
 * and simply don't appear here until a franchisee changes them.
 */
export default function AdAllocationSection({ onDrillIn }: Props) {
  const { data, isLoading, error } = useAllocationReportQuery(true);
  const rows = data?.allocations ?? [];

  const avgMeta = useMemo(() => {
    if (!rows.length) return 0;
    return Math.round(rows.reduce((s, r) => s + r.metaPct, 0) / rows.length);
  }, [rows]);

  return (
    <>
      <div className="ds-section-header" style={{ marginTop: 8 }}>
        <h2>Ad Budget Allocation</h2>
        <span className="meta">
          Google Ads vs Meta split, set by franchisees · default is 100% Google
        </span>
      </div>

      <div className="ds-panel" style={{ padding: 20 }}>
        {isLoading ? (
          <div style={{ color: '#8a95a5' }}>Loading allocations…</div>
        ) : error ? (
          <div style={{ color: '#c0392b' }}>Error loading allocations</div>
        ) : rows.length === 0 ? (
          <div style={{ color: '#8a95a5' }}>
            No custom allocations yet — every territory is currently 100% Google Ads.
          </div>
        ) : (
          <>
            <div
              style={{
                display: 'flex',
                gap: 24,
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                marginBottom: 16,
              }}
            >
              <div style={{ fontSize: 13, color: '#5a6675' }}>
                <strong>{rows.length}</strong> territor{rows.length === 1 ? 'y has' : 'ies have'} a
                custom split · avg <strong>{avgMeta}%</strong> to Meta
              </div>
              <a
                href="/api/ad-allocation/report.csv"
                download
                className="ds-btn"
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  padding: '8px 16px',
                  borderRadius: 6,
                  background: '#2980b9',
                  color: '#fff',
                  textDecoration: 'none',
                }}
              >
                ↓ Download CSV
              </a>
            </div>

            <div style={{ overflowX: 'auto' }}>
              <table className="ds-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ textAlign: 'left', color: '#8a95a5', fontSize: 12 }}>
                    <th style={thStyle}>Territory</th>
                    <th style={thStyle}>Franchisee</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>Google %</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>Meta %</th>
                    <th style={thStyle}>Updated by</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>Updated</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.afSub} style={{ borderTop: '1px solid #eef1f5', fontSize: 13.5 }}>
                      <td style={tdStyle}>
                        {onDrillIn ? (
                          <button
                            type="button"
                            onClick={() => onDrillIn(r.afBase)}
                            style={{
                              background: 'none',
                              border: 'none',
                              padding: 0,
                              color: '#2980b9',
                              fontWeight: 600,
                              cursor: 'pointer',
                            }}
                          >
                            {r.afSub}
                          </button>
                        ) : (
                          <strong>{r.afSub}</strong>
                        )}
                      </td>
                      <td style={{ ...tdStyle, color: '#5a6675' }}>{r.franchiseeName ?? '—'}</td>
                      <td style={{ ...tdStyle, textAlign: 'right', color: '#2980b9', fontWeight: 600 }}>
                        {r.googlePct}%
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'right', color: '#8e44ad', fontWeight: 600 }}>
                        {r.metaPct}%
                      </td>
                      <td style={{ ...tdStyle, color: '#8a95a5', fontSize: 12.5 }}>
                        {r.updatedBy ?? '—'}
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'right', color: '#8a95a5', fontSize: 12.5 }}>
                        {r.updatedAt ? new Date(r.updatedAt).toLocaleDateString() : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </>
  );
}

const thStyle: React.CSSProperties = { padding: '6px 10px', fontWeight: 600 };
const tdStyle: React.CSSProperties = { padding: '8px 10px' };
